import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { FC } from 'react'

import { PersonalAccountType } from '@/api/models/auth/lib/auth.types'
import { useSelectedProject } from '@/api/models/main/hooks/useSelectedProject'
import { useCreateRequestPass } from '@/api/models/requests'
import {
  CreateServicePassDTO,
  RequestByIdApiType,
  RequestTypeAlias
} from '@/api/models/requests/lib/requests.types'
import { ServerValidationError } from '@/api/shared/types'
import { useSelectedProjectStore } from '@/components/entities/common/model/selected-project.store'
import { PERMANENT_PASS_TYPES } from '@/components/entities/passes/model/constants'
import { CourierPassForm } from '@/components/entities/passes/ui/PassForms/CourierPassForm'
import { GuestPassAutoForm } from '@/components/entities/passes/ui/PassForms/GuestPassAutoForm'
import { GuestPassForm } from '@/components/entities/passes/ui/PassForms/GuestPassForm'
import { PermanentPassForm } from '@/components/entities/passes/ui/PassForms/PermanentPassForm'
import { PropertiesPassForm } from '@/components/entities/passes/ui/PassForms/PropertiesPassForm'
import { DEFAULT_DATE_ONLY_FORMAT } from '@/components/entities/requests/model/constants'
import { PassFormSubmitData } from '@/components/entities/requests/model/form.types'
import {
  getDesiredDate,
  getPersonalAccountsByRoomId
} from '@/components/entities/requests/ui/model/utils'
import { useRequestNavigation } from '@/components/widgets/request/hooks/useRequestNavigation'
import {
  hasAutoNumberServerError,
  hasPersonServerError,
  isServerValidationError
} from '@/utils/parseServerErrorField'

import { RentPassAutoForm } from './RentPassAutoForm'
import { OwnerAccessPlug } from './RentPassAutoForm/OwnerAccessPlug'

type Props = {
  typeAliasFromUrl: RequestTypeAlias
  onFormDirty: (_: boolean) => void
  onFormSuccess: (_: boolean) => void
  onFormError: (error: AxiosError<ServerValidationError>) => void
  pass?: RequestByIdApiType
}

const PassFormWrapper: FC<Props> = ({
  typeAliasFromUrl,
  onFormDirty,
  onFormSuccess,
  onFormError,
  pass
}) => {
  const { selectedProjectId: projectId } = useSelectedProjectStore()
  const { navigateToPassDetailsSuccess } = useRequestNavigation()
  const {
    mutateAsync: createPass,
    isPending,
    error: passError
  } = useCreateRequestPass()

  const { selectedProject } = useSelectedProject()

  const isRentPassAutoWithoutOwnerAccess =
    typeAliasFromUrl === RequestTypeAlias.RentPassAuto &&
    selectedProject?.personalAccounts.every(
      personalAccount =>
        String(personalAccount.type).toLowerCase() !== PersonalAccountType.Owner
    )

  const onSubmit = async (data: PassFormSubmitData) => {
    const premiseId = data?.room?.id || undefined
    const personalAccountsForRoom = getPersonalAccountsByRoomId(
      premiseId,
      selectedProject
    )

    const personalAccountId =
      personalAccountsForRoom && personalAccountsForRoom.length > 1
        ? data?.personalAccount?.id
        : personalAccountsForRoom?.[0]?.id

    const isPermanentPass = PERMANENT_PASS_TYPES.includes(typeAliasFromUrl)
    const isRentPass = typeAliasFromUrl === RequestTypeAlias.RentPassAuto

    const { date, ...passData } = data

    let desiredDate: CreateServicePassDTO['desiredDate']

    if (isPermanentPass || isRentPass) {
      desiredDate = undefined
      if (date === undefined) {
        desiredDate = undefined
      }
    } else {
      desiredDate = getDesiredDate(dayjs(date).format(DEFAULT_DATE_ONLY_FORMAT))
    }

    const requestBody: CreateServicePassDTO = {
      projectId,
      premiseId: data.room?.id,
      personalAccountId,
      desiredDate,
      typeAlias: typeAliasFromUrl,
      ...passData
    }

    try {
      const response = await createPass(requestBody)

      if (response.data?.id) {
        navigateToPassDetailsSuccess(response.data.id)
        onFormSuccess(true)
      }
    } catch (error) {
      const axiosError = error as AxiosError<ServerValidationError>
      const isValidationError = isServerValidationError(axiosError)

      if (isValidationError) {
        const shouldShowOnForm = axiosError.response?.data.errors?.some(
          ({ field }) =>
            hasAutoNumberServerError(field) ||
            hasPersonServerError(field) ||
            field === 'desiredDate'
        )

        if (shouldShowOnForm) {
          return
        }
      }

      onFormError(axiosError)
    } finally {
      onFormDirty(false)
    }
  }

  return (
    <>
      {PERMANENT_PASS_TYPES.includes(typeAliasFromUrl) && (
        <PermanentPassForm
          typeAliasFromUrl={typeAliasFromUrl}
          onFormDirty={onFormDirty}
          pass={pass}
          onSubmit={onSubmit}
          passError={passError}
          isPending={isPending}
        />
      )}
      {typeAliasFromUrl === RequestTypeAlias.CourierPass && (
        <CourierPassForm
          onFormDirty={onFormDirty}
          pass={pass}
          onSubmit={onSubmit}
          passError={passError}
          isPending={isPending}
        />
      )}
      {typeAliasFromUrl === RequestTypeAlias.GuestPass && (
        <GuestPassForm
          onFormDirty={onFormDirty}
          pass={pass}
          onSubmit={onSubmit}
          passError={passError}
          isPending={isPending}
        />
      )}
      {typeAliasFromUrl === RequestTypeAlias.GuestPassAuto && (
        <GuestPassAutoForm
          onFormDirty={onFormDirty}
          pass={pass}
          onSubmit={onSubmit}
          passError={passError}
          isPending={isPending}
        />
      )}
      {typeAliasFromUrl === RequestTypeAlias.PropertiesPass && (
        <PropertiesPassForm
          onFormDirty={onFormDirty}
          pass={pass}
          onSubmit={onSubmit}
          passError={passError}
          isPending={isPending}
        />
      )}
      {typeAliasFromUrl === RequestTypeAlias.RentPassAuto &&
        (isRentPassAutoWithoutOwnerAccess ? (
          <OwnerAccessPlug />
        ) : (
          <RentPassAutoForm
            onFormDirty={onFormDirty}
            pass={pass}
            personalAccountId={pass?.personalAccountId}
          />
        ))}
    </>
  )
}

export { PassFormWrapper }
